import { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { getMaintenanceStatus } from '../api/system';
import { getRegistrationPreview } from '../api/auth';
import { useTranslation } from '../i18n';
import { analytics } from '../hooks/useAnalytics';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import type { RegistrationPreview as RegistrationPreviewType } from '@lotngo/shared';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<1 | 2>(1);
  const [inviteCode, setInviteCode] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [inviteCodePrefilled, setInviteCodePrefilled] = useState(false);

  const [stepOneError, setStepOneError] = useState<string | null>(null);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const [stepOneLoading, setStepOneLoading] = useState(false);

  const [userData, setUserData] = useState<RegistrationPreviewType['user'] | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Self-registration state (Path B: phone not pre-enlisted)
  const [selfRegMode, setSelfRegMode] = useState(false);
  const [selfRegName, setSelfRegName] = useState('');
  const [selfRegApartment, setSelfRegApartment] = useState('');
  const [selfRegEmail, setSelfRegEmail] = useState('');
  const [selfRegBuildingName, setSelfRegBuildingName] = useState('');

  const setAuth = useAuthStore((s) => s.setAuth);
  const syncNotificationStatus = useAuthStore((s) => s.syncNotificationStatus);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill invite code and/or JWT token from URL
  useEffect(() => {
    const urlCode = searchParams.get('inviteCode');
    const jwtToken = searchParams.get('userData');

    // NEW: If JWT token provided, validate and skip to step 2
    if (jwtToken && urlCode && step === 1) {
      setStepOneLoading(true);
      try {
        // Decode JWT without verifying (browser doesn't have secret)
        // Backend will verify when we submit
        const parts = jwtToken.split('.');
        if (parts.length !== 3) {
          throw new Error('Invalid token format');
        }

        // Decode the payload (parts[1])
        const decoded = JSON.parse(atob(parts[1]));
        const { phone: tokenPhone, exp } = decoded as { phone?: string; exp?: number };

        // Check if token is expired
        if (exp && exp * 1000 < Date.now()) {
          setStepOneError(t('invalid_registration_link'));
          setStepOneLoading(false);
          return;
        }

        if (!tokenPhone) {
          setStepOneError(t('invalid_registration_link'));
          setStepOneLoading(false);
          return;
        }

        // Validate phone + code combo via getRegistrationPreview
        const trimmedCode = urlCode.toUpperCase();
        setInviteCode(trimmedCode);
        setPhone(tokenPhone);

        getRegistrationPreview(trimmedCode, tokenPhone)
          .then((res) => {
            const result = res.data as RegistrationPreviewType;
            if (result.found && result.user) {
              setUserData(result.user);
              setStep(2);
              setStepOneLoading(false);
            } else {
              const resultCode = result.code;
              if (resultCode === 'PHONE_NOT_REGISTERED') {
                setSelfRegMode(true);
                analytics.trackSelfRegFormShown();
                setSelfRegBuildingName(result.building?.name || '');
                setStep(2);
                setStepOneLoading(false);
              } else {
                const isAlreadyRegistered = resultCode === 'USER_ALREADY_REGISTERED';
                setStepOneError(isAlreadyRegistered ? t('user_already_registered') : t('invalid_registration_link'));
                setShowLoginHint(isAlreadyRegistered);
                setStepOneLoading(false);
              }
            }
          })
          .catch((err) => {
            console.error('getRegistrationPreview error:', err);
            setStepOneError(t('error_loading_registration'));
            setStepOneLoading(false);
          });
      } catch (err) {
        console.error('JWT decode error:', err);
        setStepOneError(t('invalid_registration_link'));
        setStepOneLoading(false);
      }
    } else if (urlCode && !inviteCode && step === 1) {
      // Traditional mode: code only, user enters phone
      const trimmed = urlCode.trim().toUpperCase();
      if (trimmed.length >= 4) {
        setInviteCode(trimmed);
        setInviteCodePrefilled(true);
        // Auto-focus phone field after a short delay to ensure DOM is ready
        setTimeout(() => phoneInputRef.current?.focus(), 100);
      }
    }
  }, [searchParams, t]);

  async function handleStepOneSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedCode = inviteCode.trim().toUpperCase();
    const trimmedPhone = phone.trim();

    if (trimmedCode.length < 4) {
      setStepOneError(t('need_help_code'));
      return;
    }

    if (!trimmedPhone) {
      setStepOneError(t('check_details'));
      return;
    }

    setStepOneError(null);
    setStepOneLoading(true);
    analytics.trackRegisterStarted();

    try {
      const res = await getRegistrationPreview(trimmedCode, trimmedPhone);
      const result = res.data as RegistrationPreviewType;

      if (result.found && result.user) {
        analytics.trackInviteCodeValidated();
        setInviteCode(trimmedCode);
        setUserData(result.user);
        setStep(2);
      } else {
        const resultCode = result.code;
        if (resultCode === 'PHONE_NOT_REGISTERED') {
          analytics.trackInviteCodeValidated();
          setInviteCode(trimmedCode);
          setSelfRegMode(true);
          setSelfRegBuildingName(result.building?.name || '');
          setStep(2);
        } else {
          const isAlreadyRegistered = resultCode === 'USER_ALREADY_REGISTERED';
          analytics.trackInviteCodeInvalid(resultCode);
          setStepOneError(isAlreadyRegistered ? t('user_already_registered') : t('phone_not_registered'));
          setShowLoginHint(isAlreadyRegistered);
        }
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const code = err.response.data?.error?.code as string | undefined;
        if (code === 'INVITE_CODE_INVALID' || code === 'INVITE_CODE_EXPIRED' || code === 'INVITE_CODE_EXHAUSTED') {
          analytics.trackInviteCodeInvalid(code);
          setStepOneError(t('code_no_longer_valid'));
        } else {
          analytics.trackInviteCodeInvalid(code || 'UNKNOWN');
          setStepOneError(t('something_wrong'));
        }
      } else {
        analytics.trackInviteCodeInvalid('NETWORK_ERROR');
        setStepOneError(t('something_wrong'));
      }
    } finally {
      setStepOneLoading(false);
    }
  }

  function validatePassword(pw: string): string | null {
    if (pw.length < 8 || pw.length > 12) return t('password_requirements');
    if (!/[a-zA-Z]/.test(pw)) return t('password_requirements');
    if (!/[0-9]/.test(pw)) return t('password_requirements');
    if (!/[^a-zA-Z0-9]/.test(pw)) return t('password_requirements');
    return null;
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();

    if (!password.trim()) {
      setPasswordError(t('check_details'));
      return;
    }

    if (password !== passwordConfirm) {
      setPasswordError(t('passwords_do_not_match'));
      return;
    }

    const pwError = validatePassword(password);
    if (pwError) {
      setPasswordError(pwError);
      return;
    }

    setPasswordError(null);
    setSubmitLoading(true);

    if (selfRegMode) {
      if (!selfRegName.trim()) { setPasswordError(t('name_required')); setSubmitLoading(false); return; }
      if (!selfRegApartment.trim()) { setPasswordError(t('apartment_required')); setSubmitLoading(false); return; }
      if (!selfRegEmail.trim()) { setPasswordError(t('email_required')); setSubmitLoading(false); return; }
    }

    try {
      const jwtToken = searchParams.get('userData');
      const res = await apiClient.post('/auth/register', {
        invite_code: inviteCode,
        phone: selfRegMode ? phone : userData?.phone,
        user_data: jwtToken || undefined,
        password,
        remember_me: rememberMe,
        ...(selfRegMode && {
          name: selfRegName.trim(),
          email: selfRegEmail.trim(),
          apartment: selfRegApartment.trim(),
        }),
      });

      const { access_token, refresh_token, user } = res.data.data as {
        access_token: string;
        refresh_token: string;
        user: Parameters<typeof setAuth>[2];
      };

      setAuth(access_token, refresh_token, user);
      analytics.trackUserRegistered(selfRegMode ? 'self_registered' : 'admin_enlisted');
      // P3.3 + P3.5: set GA4 user properties
      analytics.setUserId(user.id);
      analytics.setUserProperties({
        buildingId: user.building.id,
        userRole: user.super_admin ? 'super_admin' : user.is_admin ? 'admin' : 'resident',
      });
      await syncNotificationStatus();
      navigate('/', { replace: true });
    } catch (err) {
      if (!axios.isAxiosError(err) || !err.response) {
        setPasswordError(t('something_wrong'));
        return;
      }

      const code = err.response.data?.error?.code as string | undefined;
      if (code === 'INVITE_CODE_INVALID' || code === 'INVITE_CODE_EXPIRED' || code === 'INVITE_CODE_EXHAUSTED') {
        setStepOneError(t('code_no_longer_valid'));
        setStep(1);
        setUserData(null);
        setSelfRegMode(false);
      } else if (code === 'PHONE_NOT_FOUND' || code === 'INVALID_PHONE_COMBINATION') {
        setPasswordError(t('matching_invitation_not_found'));
        setStep(1);
        setUserData(null);
        setSelfRegMode(false);
      } else if (code === 'PHONE_ALREADY_EXISTS') {
        setStepOneError(t('phone_already_registered'));
        setStep(1);
        setUserData(null);
        setSelfRegMode(false);
      } else if (code === 'EMAIL_ALREADY_EXISTS') {
        setPasswordError(t('email_already_exists'));
      } else if (code === 'USER_ALREADY_REGISTERED') {
        setStepOneError(t('user_already_registered'));
        setShowLoginHint(true);
        setStep(1);
        setUserData(null);
        setSelfRegMode(false);
      } else {
        setPasswordError(t('registration_failed'));
      }
    } finally {
      setSubmitLoading(false);
    }
  }

  // Step 1: Invite code + Phone number
  if (step === 1) {
    return (
      <div style={s.page}>
        <div style={s.blobTopRight} />
        <div style={s.blobBottomLeft} />

        <main style={s.main}>
          <div style={s.brand}>
            <svg width="64" height="64" viewBox="0 0 69 71" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '1.5rem' }}>
              <defs>
                <linearGradient id="navGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#005bc0' }} />
                  <stop offset="100%" style={{ stopColor: '#004fa9' }} />
                </linearGradient>
              </defs>
              <rect x="0" y="2" width="58" height="58" rx="14" fill="url(#navGrad)" />
              <text x="29" y="46" textAnchor="middle" fontFamily="Georgia,serif" fontSize="36" fontWeight="700" fill="#f7f7ff">P</text>
              <circle cx="50" cy="58" r="11" fill="#ffffff" />
              <path d="M44 58 L50 52 L50 55.5 L57 55.5 L57 60.5 L50 60.5 L50 64 Z" fill="#005bc0" />
            </svg>
            <h1 style={s.headline}>{t('join_lotngo')}</h1>
            <p style={s.brandSubtitle}>{inviteCodePrefilled ? t('fill_phone_and_code') : t('enter_invite_code')}</p>
          </div>

          <div style={s.card}>
            <form onSubmit={handleStepOneSubmit} style={s.form}>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel} htmlFor="invite-code">{t('invite_code')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="invite-code"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => { if (!inviteCodePrefilled) setInviteCode(e.target.value.toUpperCase()); setStepOneError(null); setShowLoginHint(false); }}
                    disabled={inviteCodePrefilled}
                    style={{
                      ...s.input,
                      ...(inviteCodePrefilled ? s.inputDisabled : {}),
                      ...(stepOneError && !inviteCodePrefilled ? s.inputError : {})
                    }}
                    placeholder={t('invite_code_example')}
                    autoComplete="off"
                    autoCapitalize="characters"
                    autoFocus={!inviteCodePrefilled}
                  />
                  <span className="material-symbols-outlined" style={s.inputIcon}>vpn_key</span>
                </div>
                <p style={s.helperText}>{t('fill_phone_and_code')}</p>
              </div>

              <div style={s.fieldGroup}>
                <label style={s.fieldLabel} htmlFor="phone">{t('phone_number')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={phoneInputRef}
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setStepOneError(null); setShowLoginHint(false); }}
                    style={{ ...s.input, ...(stepOneError ? s.inputError : {}) }}
                    placeholder="0557654321"
                    autoComplete="tel"
                    autoFocus={inviteCodePrefilled}
                  />
                  <span className="material-symbols-outlined" style={s.inputIcon}>phone</span>
                </div>
              </div>

              {stepOneError && <p style={s.fieldError}>{stepOneError}</p>}

              {showLoginHint && (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={s.loginHintBtn}
                >
                  {t('go_to_login')}
                </button>
              )}

              <button type="submit" style={s.primaryBtn} disabled={stepOneLoading}>
                <span>{stepOneLoading ? t('checking') : t('continue')}</span>
                {!stepOneLoading && (
                  <DirectionalIcon icon="arrow_forward" style={{ fontSize: 20 }} />
                )}
              </button>
            </form>

            <div style={s.secondaryActions}>
              <button onClick={() => navigate('/login')} style={s.backLink}>
                <DirectionalIcon icon="arrow_back" style={{ fontSize: 18 }} />
                <span>{t('back_to_login')}</span>
              </button>
            </div>
          </div>

          <p style={s.footer}>
            {t('need_help_code')}{' '}
            <a href="mailto:support@lotngo.com" style={s.footerLink}>{t('contact_support')}</a>
          </p>
        </main>
      </div>
    );
  }

  // Step 2: Password creation (Path A: pre-filled info) or full form (Path B: self-registration)
  return (
    <div style={s.page}>
      <div style={s.blobTopRight} />
      <div style={s.blobBottomLeft} />

      <main style={s.main}>
        <div style={s.brand}>
          <h1 style={s.headline}>{selfRegMode ? t('self_register_title') : t('set_password')}</h1>
          <p style={s.brandSubtitle}>{selfRegMode ? t('self_register_subtitle') : t('create_your_password')}</p>
        </div>

        <div style={s.card}>
          {selfRegMode ? (
            <>
              {selfRegBuildingName && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={s.buildingBadge}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>apartment</span>
                    <span>{t('joining_building')} <strong>{selfRegBuildingName}</strong></span>
                  </div>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} style={s.form}>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel} htmlFor="self-reg-name">{t('full_name')} *</label>
                  <div style={s.iconInput}>
                    <span className="material-symbols-outlined" style={s.iconInputIcon}>person</span>
                    <input
                      id="self-reg-name"
                      type="text"
                      value={selfRegName}
                      onChange={(e) => { setSelfRegName(e.target.value); setPasswordError(null); }}
                      style={s.iconInputField}
                      placeholder={t('name_example')}
                      autoComplete="name"
                      autoFocus
                    />
                  </div>
                </div>

                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel} htmlFor="self-reg-apartment">{t('apartment_number')} *</label>
                  <div style={s.iconInput}>
                    <span className="material-symbols-outlined" style={s.iconInputIcon}>door_front</span>
                    <input
                      id="self-reg-apartment"
                      type="text"
                      value={selfRegApartment}
                      onChange={(e) => { setSelfRegApartment(e.target.value); setPasswordError(null); }}
                      style={s.iconInputField}
                      placeholder={t('apartment_example')}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel} htmlFor="self-reg-email">{t('email_address')} *</label>
                  <div style={s.iconInput}>
                    <span className="material-symbols-outlined" style={s.iconInputIcon}>mail</span>
                    <input
                      id="self-reg-email"
                      type="email"
                      value={selfRegEmail}
                      onChange={(e) => { setSelfRegEmail(e.target.value); setPasswordError(null); }}
                      style={s.iconInputField}
                      placeholder={t('email_example')}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--surface-low)', paddingTop: '1.25rem' }}>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel} htmlFor="password">{t('password')}</label>
                    <div style={{ ...s.iconInput, ...(passwordError ? s.iconInputError : {}) }}>
                      <span className="material-symbols-outlined" style={s.iconInputIcon}>lock</span>
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
                        style={s.iconInputField}
                        placeholder={t('password_placeholder')}
                        autoComplete="new-password"
                        minLength={8}
                        maxLength={12}
                      />
                    </div>
                    <p style={s.helperText}>{t('password_requirements')}</p>
                  </div>
                </div>

                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel} htmlFor="password-confirm">{t('confirm_password')}</label>
                  <div style={{ ...s.iconInput, ...(passwordError ? s.iconInputError : {}) }}>
                    <span className="material-symbols-outlined" style={s.iconInputIcon}>lock</span>
                    <input
                      id="password-confirm"
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => { setPasswordConfirm(e.target.value); setPasswordError(null); }}
                      style={s.iconInputField}
                      placeholder={t('password_placeholder')}
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={12}
                    />
                  </div>
                </div>

                {passwordError && <p style={s.fieldError}>{passwordError}</p>}

                <div style={s.termsRow}>
                  <input id="remember-me" type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={s.checkbox} />
                  <label htmlFor="remember-me" style={s.termsLabel}>{t('keep_logged_in')}</label>
                </div>

                <button type="submit" style={s.primaryBtn} disabled={submitLoading}>
                  <span>{submitLoading ? t('creating_account') : t('create_account')}</span>
                  {!submitLoading && <DirectionalIcon icon="arrow_forward" style={{ fontSize: 20 }} />}
                </button>
              </form>

              <div style={s.secondaryActions}>
                <button
                  onClick={() => { setStep(1); setSelfRegMode(false); setPassword(''); setPasswordConfirm(''); }}
                  style={s.backLink}
                >
                  <DirectionalIcon icon="arrow_back" style={{ fontSize: 18 }} />
                  <span>{t('back')}</span>
                </button>
              </div>
            </>
          ) : userData && (
            <>
              {/* Path A: Account info display */}
              <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--surface-low)' }}>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--on-surface-2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t('account_registered_as')}
                </p>
                <div style={{ backgroundColor: 'var(--surface-low)', borderRadius: '8px', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: 20 }}>mail</span>
                  <span style={{ color: 'var(--on-surface)', fontWeight: 500, fontSize: '0.95rem' }}>
                    {userData.email}
                  </span>
                </div>
              </div>

              <form onSubmit={handlePasswordSubmit} style={s.form}>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel} htmlFor="password">{t('password')}</label>
                  <div style={{ ...s.iconInput, ...(passwordError ? s.iconInputError : {}) }}>
                    <span className="material-symbols-outlined" style={s.iconInputIcon}>lock</span>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
                      style={s.iconInputField}
                      placeholder={t('password_placeholder')}
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={12}
                      autoFocus
                    />
                  </div>
                  <p style={s.helperText}>{t('password_requirements')}</p>
                </div>

                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel} htmlFor="password-confirm">{t('confirm_password')}</label>
                  <div style={{ ...s.iconInput, ...(passwordError ? s.iconInputError : {}) }}>
                    <span className="material-symbols-outlined" style={s.iconInputIcon}>lock</span>
                    <input
                      id="password-confirm"
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => { setPasswordConfirm(e.target.value); setPasswordError(null); }}
                      style={s.iconInputField}
                      placeholder={t('password_placeholder')}
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={12}
                    />
                  </div>
                </div>

                {passwordError && <p style={s.fieldError}>{passwordError}</p>}

                <div style={s.termsRow}>
                  <input id="remember-me" type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={s.checkbox} />
                  <label htmlFor="remember-me" style={s.termsLabel}>{t('keep_logged_in')}</label>
                </div>

                <button type="submit" style={s.primaryBtn} disabled={submitLoading}>
                  <span>{submitLoading ? t('creating_account') : t('create_account')}</span>
                  {!submitLoading && <DirectionalIcon icon="arrow_forward" style={{ fontSize: 20 }} />}
                </button>
              </form>

              <div style={s.secondaryActions}>
                <button
                  onClick={() => { setStep(1); setUserData(null); setPassword(''); setPasswordConfirm(''); }}
                  style={s.backLink}
                >
                  <DirectionalIcon icon="arrow_back" style={{ fontSize: 18 }} />
                  <span>{t('back')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: {
    position:        'relative',
    minHeight:       '100dvh',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    backgroundColor: 'var(--surface)',
    fontFamily:      'var(--inter)',
    padding:         '1.5rem',
    overflow:        'hidden',
  },
  blobTopRight: {
    position:        'fixed',
    top:             '-10%',
    right:           '-5%',
    width:           '40%',
    height:          '40%',
    borderRadius:    '9999px',
    background:      'rgba(221,232,255,0.35)',
    filter:          'blur(120px)',
    pointerEvents:   'none',
    zIndex:          0,
  },
  blobBottomLeft: {
    position:        'fixed',
    bottom:          '-10%',
    left:            '-5%',
    width:           '40%',
    height:          '40%',
    borderRadius:    '9999px',
    background:      'rgba(216,211,244,0.25)',
    filter:          'blur(120px)',
    pointerEvents:   'none',
    zIndex:          0,
  },
  // Step 1 layout
  main: {
    position:  'relative',
    zIndex:    1,
    width:     '100%',
    maxWidth:  '448px',
  },
  brand: {
    textAlign:    'center',
    marginBottom: '3rem',
  },
  iconWrap: {
    display:         'inline-flex',
    alignItems:      'center',
    justifyContent:  'center',
    width:           64,
    height:          64,
    borderRadius:    16,
    backgroundColor: 'var(--primary-container)',
    color:           'var(--primary)',
    marginBottom:    '1.5rem',
    boxShadow:       '0 2px 8px rgba(0,91,192,0.10)',
  },
  headline: {
    margin:        '0 0 0.75rem',
    fontSize:      '2.25rem',
    fontWeight:    800,
    fontFamily:    'var(--manrope)',
    color:         'var(--on-surface)',
    letterSpacing: '-0.02em',
    lineHeight:    1.15,
  },
  brandSubtitle: {
    margin:   0,
    color:    'var(--on-surface-2)',
    fontSize: '1.05rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius:    '24px',
    padding:         '2rem',
    width:           '100%',
    boxSizing:       'border-box',
    boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  },
  form: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '1.25rem',
  },
  input: {
    height:          56,
    padding:         '0 3rem 0 1.25rem',
    borderRadius:    '12px',
    border:          'none',
    backgroundColor: 'var(--surface-low)',
    fontSize:        '1rem',
    fontWeight:      500,
    color:           'var(--on-surface)',
    outline:         '2px solid transparent',
    transition:      'outline 0.15s, background-color 0.15s',
    width:           '100%',
    boxSizing:       'border-box' as const,
    fontFamily:      'var(--inter)',
  },
  inputWhite: {
    backgroundColor: '#ffffff',
    borderRadius:    '12px',
    padding:         '0 1.25rem',
  },
  inputError: {
    outline: '2px solid var(--error)',
  },
  inputDisabled: {
    backgroundColor: 'var(--surface-container-lowest)',
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  inputIcon: {
    position:      'absolute',
    right:         '1rem',
    top:           '50%',
    transform:     'translateY(-50%)',
    color:         'var(--on-surface-3)',
    pointerEvents: 'none',
    fontSize:      22,
  },
  helperText: {
    margin:   0,
    fontSize: '0.8rem',
    color:    'var(--on-surface-3)',
  },
  fieldError: {
    margin:   0,
    color:    'var(--error)',
    fontSize: '0.8rem',
  },
  generalError: {
    color:    'var(--error)',
    fontSize: '0.875rem',
    margin:   '0',
  },
  loginHintBtn: {
    display:      'block',
    width:        '100%',
    padding:      '0.65rem',
    marginTop:    '0.25rem',
    background:   'var(--primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: '0.75rem',
    fontSize:     '0.9rem',
    fontWeight:   600,
    fontFamily:   'var(--inter)',
    cursor:       'pointer',
  },
  secondaryActions: {
    marginTop:      '2rem',
    display:        'flex',
    justifyContent: 'center',
  },
  backLink: {
    display:    'inline-flex',
    alignItems: 'center',
    gap:        '0.4rem',
    background: 'none',
    border:     'none',
    color:      'var(--primary)',
    fontSize:   '0.9rem',
    fontWeight: 600,
    fontFamily: 'var(--inter)',
    cursor:     'pointer',
    padding:    0,
  },
  footer: {
    marginTop: '3rem',
    textAlign: 'center',
    color:     'var(--on-surface-3)',
    fontSize:  '0.875rem',
  },
  footerLink: {
    color:               'var(--primary)',
    fontWeight:          500,
    textDecoration:      'underline',
    textUnderlineOffset: '3px',
  },

  buildingBadge: {
    display:         'inline-flex',
    alignItems:      'center',
    gap:             '0.4rem',
    padding:         '6px 14px',
    borderRadius:    '8px',
    backgroundColor: 'var(--primary-container)',
    color:           'var(--primary)',
    fontSize:        '0.85rem',
    fontWeight:      500,
  },
  badgeDivider: {
    color:   'var(--on-surface-3)',
    margin:  '0 2px',
  },
  nameAptCell: {
    backgroundColor: 'var(--surface-low)',
    borderRadius:    '16px',
    padding:         '1.25rem',
    display:         'flex',
    flexDirection:   'column' as const,
    gap:             '1rem',
    boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  },
  contactCard: {
    backgroundColor: '#ffffff',
    borderRadius:    '16px',
    padding:         '1.5rem',
    display:         'flex',
    flexDirection:   'column' as const,
    gap:             '1.25rem',
    boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  },
  twoCol: {
    display:               'grid',
    gridTemplateColumns:   '1fr 1fr',
    gap:                   '1rem',
  },
  fieldGroup: {
    display:       'flex',
    flexDirection: 'column' as const,
    gap:           '6px',
  },
  fieldLabel: {
    fontSize:      '0.65rem',
    fontWeight:    600,
    color:         'var(--on-surface-2)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },
  iconInput: {
    display:         'flex',
    alignItems:      'center',
    gap:             '0.6rem',
    backgroundColor: 'var(--surface-low)',
    borderRadius:    '12px',
    padding:         '0 1rem',
    height:          48,
    outline:         '2px solid transparent',
    transition:      'outline 0.15s',
  },
  iconInputError: {
    outline: '2px solid var(--error)',
  },
  iconInputIcon: {
    color:     'var(--on-surface-3)',
    fontSize:  20,
    flexShrink: 0,
  },
  iconInputField: {
    flex:        1,
    border:      'none',
    background:  'transparent',
    fontSize:    '0.95rem',
    fontWeight:  500,
    color:       'var(--on-surface)',
    outline:     'none',
    fontFamily:  'var(--inter)',
    minWidth:    0,
  },
  termsRow: {
    display:    'flex',
    alignItems: 'flex-start',
    gap:        '0.75rem',
    padding:    '0 0.25rem',
  },
  checkbox: {
    width:        20,
    height:       20,
    marginTop:    2,
    accentColor:  'var(--primary)',
    flexShrink:   0,
    cursor:       'pointer',
  },
  termsLabel: {
    fontSize:   '0.8rem',
    color:      'var(--on-surface-2)',
    lineHeight: 1.6,
    cursor:     'pointer',
  },
  termsLink: {
    color:      'var(--primary)',
    fontWeight: 600,
    cursor:     'pointer',
  },
  primaryBtn: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '0.5rem',
    width:          '100%',
    height:         56,
    borderRadius:   '12px',
    border:         'none',
    background:     'linear-gradient(135deg, #005bc0, #004fa9)',
    color:          'var(--on-primary)',
    fontSize:       '1rem',
    fontWeight:     700,
    fontFamily:     'var(--inter)',
    cursor:         'pointer',
    boxShadow:      '0 4px 16px rgba(0,91,192,0.25)',
    transition:     'opacity 0.15s',
  },
  primaryBtnDisabled: {
    opacity: 0.45,
    cursor:  'not-allowed',
    boxShadow: 'none',
  },
  inlineLink: {
    background:  'none',
    border:      'none',
    color:       'var(--primary)',
    fontWeight:  700,
    fontSize:    '0.875rem',
    fontFamily:  'var(--inter)',
    cursor:      'pointer',
    padding:     0,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  prefillRow: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    padding:        '0.75rem 0',
    borderBottom:   '1px solid var(--surface-low)',
  },
  prefillLabel: {
    fontSize:   '0.85rem',
    fontWeight: 600,
    color:      'var(--on-surface-2)',
  },
  prefillValue: {
    fontSize:   '0.95rem',
    fontWeight: 500,
    color:      'var(--on-surface)',
  },
};
